<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-1">
      <div>
        <h1 class="headline">Markups</h1>
      </div>
      <v-col class="py-0">
        <list-filters
          :filters="filters"
          label="Find Markup"
          ref-name="markups_list_filters"
          @searchParamsChanged="onFiltersChange"
          :expansionPanel="false"
        ></list-filters>
      </v-col>
      <div>
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              class="ml-2"
              v-on="on"
              fab
              dark
              color="primary"
              @click="onCreateNew"
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Create New Markup</span>
        </v-tooltip>
      </div>
    </v-row>

    <v-row>
      <v-col class="pa-0">
        <v-data-table
          :headers="filteredHeaders"
          :items="items"
          :loading="$apollo.loading"
          :search="search"
          item-key="id"
          class="elevation-1"
          v-bind="pagingOptions"
          mobile-break-point="1264"
          fixed-header
          hide-default-footer
          :height="gridHeight"
          show-select
          v-model="selected"
        >
          <template v-slot:item.groupName="{ item }">
            <v-row align="center">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="grey lighten-1" class="px-1" small
                    >mdi-information-outline</v-icon
                  >
                </template>
                <v-container>
                  {{
                    item.group && item.group.members.edges.length > 0
                      ? item.group.members.edges
                          .map((g) => g.node.name)
                          .join(", ")
                      : "No members."
                  }}
                </v-container>
              </v-tooltip>
              {{ item.groupName }}
            </v-row>
          </template>
          <template v-slot:item.validFrom="{ item }">{{
            item.validFrom | formatDate
          }}</template>
          <template v-slot:item.validTo="{ item }">{{
            item.validTo | formatDate
          }}</template>
          <template v-slot:item.itineraryCreatedFrom="{ item }">{{
            item.itineraryCreatedFrom | formatDate
          }}</template>
          <template v-slot:item.itineraryCreatedTo="{ item }">{{
            item.itineraryCreatedTo | formatDate
          }}</template>
          <template v-slot:item.notInUse="{ item }">
            <v-simple-checkbox
              v-model="item.notInUse"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.historicalOnly="{ item }">
            <v-simple-checkbox
              v-model="item.historicalOnly"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hide-view
              @deleteItem="deleteItem"
              @editItem="editItem"
            >
              <template slot="extraButton">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="mx-0"
                      @click="onCloneItem(item)"
                    >
                      <v-icon color="primary" small>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Copy</span>
                </v-tooltip></template
              >
            </default-list-actions>
          </template>
          <template v-slot:footer>
            <list-actions
              v-if="selected.length > 0"
              :selected="selected"
              list="markups"
              :columns="visibleColumns"
              :filter="searchParams"
              @close="selected = []"
              hideUnexport
            >
            </list-actions>
            <v-row justify="center" v-if="selected.length > 0">
              <h3 class="py-2 caption">
                {{ selected.length }} record(s) selected
              </h3>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="markups"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('markups')"
    ></get-more-results>
    <edit-markup-dialog
      v-model="editedMarkup"
      @close="showMarkupEditor = false"
      :show="showMarkupEditor"
      @save="onSave"
      :title="editorTitle"
    />
    <v-row v-if="debug">
      <v-col>
        <code>gridHeight: {{ gridHeight }}</code>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import { sortByDate, removeGQLTypename } from "../../../../helpers/index";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import EditMarkupDialog from "./EditMarkupDialog";
import ListFilters from "../../shared/list-filters/ListFilters";
import listFilters from "./list-filters";
import gql from "graphql-tag";
import ListActions from "../../shared/list-actions/ListActions.vue";
const ALL_MARKUPS_QUERY = gql`
  query markupsList($after: String, $filter: MarkupFilterType) {
    markups(first: 50, after: $after, filter: $filter) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          groupName
          groupId
          group {
            members {
              edges {
                node {
                  name
                }
              }
            }
          }
          department
          destinationName
          destinationId
          validFrom
          validTo
          useDateRange
          round
          activity
          accommodation
          travel
          transport
          package
          other
          tagsList
          notInUse
          historicalOnly
          itineraryCreatedFrom
          itineraryCreatedTo
        }
      }
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createMarkup($input: MarkupInput) {
    createMarkup(markupInput: $input) {
      id
      groupName
      groupId
      department
      destinationName
      destinationId
      validFrom
      validTo
      useDateRange
      activity
      accommodation
      travel
      transport
      package
      other
      tagsList
      round
      notInUse
      historicalOnly
      itineraryCreatedFrom
      itineraryCreatedTo
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateMarkup($id: ID!, $input: MarkupInput) {
    updateMarkup(id: $id, markupInput: $input) {
      id
      groupName
      groupId
      department
      destinationName
      destinationId
      validFrom
      validTo
      useDateRange
      activity
      accommodation
      travel
      transport
      package
      other
      tagsList
      round
      notInUse
      historicalOnly
      itineraryCreatedFrom
      itineraryCreatedTo
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteMarkup($id: ID!) {
    deleteMarkup(id: $id) {
      id
    }
  }
`;
export default {
  name: "markup-list",

  mixins: [ListMixin],

  components: {
    ListVisibleColumnsAndFilter,
    GetMoreResults,
    DefaultListActions,
    EditMarkupDialog,
    ListFilters,
    ListActions,
  },

  data() {
    return {
      debug: false,
      selected: [],
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "groupName",
        "validFrom",
        "validTo",
        "department",
        "destinatioName",
        "accommodation",
        "activity",
        "transport",
        "travel",
        "actions",
      ]),
      headers: [
        {
          text: "Agent Group",
          value: "groupName",
          name: "groupName",
        },
        {
          text: "From",
          value: "validFrom",
          name: "validFrom",
          sort: sortByDate,
        },
        {
          text: "To",
          value: "validTo",
          name: "validTo",
          sort: sortByDate,
        },
        {
          text: "Iti Created From",
          value: "itineraryCreatedFrom",
          name: "itineraryCreatedFrom",
          sort: sortByDate,
        },
        {
          text: "Iti Created To",
          value: "itineraryCreatedTo",
          name: "itineraryCreatedTo",
          sort: sortByDate,
        },
        {
          text: "Department",
          value: "department",
          name: "department",
        },
        {
          text: "Destination",
          value: "destinationName",
          name: "destinationName",
        },
        {
          text: "Accommodation %",
          value: "accommodation",
          name: "accommodation",
        },
        {
          text: "Activity %",
          value: "activity",
          name: "activity",
        },
        {
          text: "Transport %",
          value: "transport",
          name: "transport",
        },
        {
          text: "Travel %",
          value: "travel",
          name: "travel",
        },
        {
          text: "Other %",
          value: "other",
          name: "other",
        },
        {
          text: "Package %",
          value: "package",
          name: "package",
        },
        {
          text: "Tags",
          value: "tagsList",
          name: "tagsList",
        },
        {
          text: "Not In Use",
          value: "notInUse",
          name: "notInUse",
        },
        {
          text: "Historical Only",
          value: "historicalOnly",
          name: "historicalOnly",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
      newMarkup: {
        department: "All",
        groupId: "",
        accommodation: "",
        activity: "",
        transport: "",
        travel: "",
        other: "",
        package: "",
        useDateRange: false,
        tagsList: "",
        round: false,
        notInUse: false,
        historicalOnly: false,
      },
      editedMarkup: {},
      showMarkupEditor: false,
      editorTitle: "Edit Markup",
      windowHeight: window.innerHeight,
    };
  },

  computed: {
    gridHeight() {
      return this.items && this.items.length >= 10
        ? this.windowHeight - 200
        : null;
    },
    items() {
      return this.markups && this.markups.edges
        ? this.markups.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return listFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    onFiltersChange(filters) {
      this.searchParams = { ...filters };
    },
    onCreateNew() {
      this.editorTitle = "Create New Markup";
      this.editedMarkup = { ...this.newMarkup };
      this.showMarkupEditor = true;
    },
    onCloneItem(item) {
      this.editorTitle = "Copy Markup";
      this.editedMarkup = { ...item, id: undefined };
      this.showMarkupEditor = true;
    },
    editItem(payload) {
      this.editorTitle = "Edit Markup";
      this.editedMarkup = { ...payload };
      this.showMarkupEditor = true;
    },
    async deleteItem(payload) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
          refetchQueries: [
            {
              query: ALL_MARKUPS_QUERY,
              variables: {
                after: this.after,
                filter: this.searchParams,
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Markup deleted",
        });
      } catch (error) {
        console.log(error);
      }
    },
    async onSave(payload) {
      this.showMarkupEditor = false;
      const { id, ...input } = payload;
      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      console.log("onSave", payload);
      try {
        const mutationRes = await this.$apollo.mutate({
          mutation: mutation,
          variables: { input: { ...input }, id },
          refetchQueries: [
            {
              query: ALL_MARKUPS_QUERY,
              variables: {
                after: this.after,
                filter: this.searchParams,
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Markup saved.",
        });
      } catch (error) {
        console.log(error);
      }
    },
  },

  apollo: {
    markups: {
      query: ALL_MARKUPS_QUERY,
      variables() {
        return {
          after: this.after,
          filter: this.searchParams,
        };
      },
      result({ data, loader, networkStatus }) {
        this.totalItems = data.markups.totalCount;
        //  this.pagination.totalItems = data.itineraries.totalCount;
      },
      fetchPolicy: "network-only",
    },
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.windowHeight = window.innerHeight;
    });
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/plugins/markups/MarkupList.vue